/**
 * O(n) flattening of the `reporting/orgList` response into the linear org rows used by the
 * global-filter tree. Kept free of Angular dependencies so filter.worker can import it too.
 *
 * The previous implementations called `Array.filter`/`find`/`some` on the growing output array
 * for every (node, parentId) pair, which was O(n²) and froze the UI for ~12s on large org trees.
 * Lookups here go through Maps instead; output order, row shape, shared `mProps` references and
 * `hasChild` flags are identical.
 */

// Ids used to be compared with `==`; keying by String() reproduces that for string/number ids,
// and null/undefined share one key (null == undefined).
const NULLISH_KEY = '\u0000nullish';
const idKey = (value: any): string => (value == null ? NULLISH_KEY : String(value));

interface IdBucket {
  rows: any[];
  // rows[0 .. marked) already have hasChild = true; flags are never reset during flattening.
  marked: number;
}

export interface LinearOrgs {
  rows: any[];
  byId: Map<string, IdBucket>;
}

/**
 * Builds one row per (node, parentId) pair from a list already sorted by Level.
 * Nodes with an empty `ParentIds` become roots; nodes with a missing `ParentIds` are skipped
 * unless `missingParentIdsAsRoot` is set (SSO login behaviour).
 */
export function linearizeOrgList(list: any[], missingParentIdsAsRoot = false): LinearOrgs {
  const rows: any[] = [];
  const byId = new Map<string, IdBucket>();

  const add = (row: any) => {
    rows.push(row);
    const key = idKey(row.id);
    const bucket = byId.get(key);
    if (bucket) {
      bucket.rows.push(row);
    } else {
      byId.set(key, { rows: [row], marked: 0 });
    }
  };

  for (let i = 0; i < list.length; i++) {
    const element = list[i];
    const parentIds = missingParentIdsAsRoot ? element.ParentIds || [] : element?.ParentIds;

    if (parentIds?.length == 0) {
      add({
        id: element.Id,
        level: element.Level,
        label: element.Label,
        name: element.Label,
        value: element.NodeId,
        Title: element.Title,
        NodeName: element.Name,
        hasChild: true,
        orginalLevel: element.Level,
        mProps: element.mProps || {
          ...element,
          title: element.Title,
          NodeName: element.Name,
          hasChild: true,
        },
        parent: null,
      });
    } else if (parentIds) {
      for (let j = 0; j < parentIds.length; j++) {
        const id = parentIds[j];
        const mProps = element.mProps || {
          ...element,
          title: element.Title,
          NodeName: element.Name,
          hasChild: false,
        };
        mProps.parent = id;
        add({
          id: element.Id,
          level: element.Level,
          label: element.Label,
          name: element.Label,
          value: element.NodeId,
          Title: element.Title,
          NodeName: element.Name,
          hasChild: false,
          orginalLevel: element.Level,
          mProps,
          parent: id,
        });

        // Every row emitted so far for the parent id gets hasChild (was a full filter() scan).
        const parents = byId.get(idKey(id));
        if (parents) {
          for (let k = parents.marked; k < parents.rows.length; k++) {
            parents.rows[k].hasChild = true;
          }
          parents.marked = parents.rows.length;
          mProps.hasChild = true;
        }
      }
    }
  }

  return { rows, byId };
}

const firstById = (byId: Map<string, IdBucket>, id: any) => byId.get(idKey(id))?.rows[0];

/**
 * Global-filter (helper service / worker) DataAccessLevels restriction: keeps rows below the
 * user's access level plus the ancestor chain of each granted node, sorted by level.
 */
export function restrictOrgsToDataAccess(linear: LinearOrgs, dataAccessLevels: any): any[] {
  const DataAccessLevels = Array.isArray(dataAccessLevels) ? dataAccessLevels : [];
  const parentId = DataAccessLevels.length >= 1 ? DataAccessLevels[0].ParentId : null;
  if (!parentId) {
    return linear.rows;
  }

  const treeSelection: any[] = [];
  const selectedIds = new Set<string>();
  const select = (row: any) => {
    treeSelection.push(row);
    if (row) {
      selectedIds.add(idKey(row.id));
    }
  };

  DataAccessLevels.forEach((dataAccessLevel: any) => {
    let dataFromParent: any = linear.byId
      .get(idKey(dataAccessLevel.Id))
      ?.rows.find((x: any) => x.parent == dataAccessLevel.ParentId);
    if (dataFromParent) {
      select(dataFromParent);
      do {
        dataFromParent = firstById(linear.byId, dataFromParent?.parent);
        if (!selectedIds.has(idKey(dataFromParent?.id))) {
          select(dataFromParent);
        }
      } while (dataFromParent?.parent);
    }
  });

  const result: any[] = [];
  const accessLevel = DataAccessLevels[0].Level;
  for (const row of linear.rows) {
    if (accessLevel < row.level) {
      result.push(row);
    }
  }
  for (const row of treeSelection) {
    result.push(row);
  }
  return result.sort((a: any, b: any) => a.level - b.level);
}

/**
 * SSO login DataAccessLevels restriction: matches each access level by ParentId only and stops
 * walking up when an ancestor is missing.
 */
export function restrictOrgsToDataAccessByParent(linear: LinearOrgs, dataAccessLevels: any[]): any[] {
  const parentId = dataAccessLevels.length >= 1 ? dataAccessLevels[0].ParentId : null;
  if (!parentId || linear.rows.length === 0) {
    return linear.rows;
  }

  const firstByParent = new Map<string, any>();
  for (const row of linear.rows) {
    const key = idKey(row.parent);
    if (!firstByParent.has(key)) {
      firstByParent.set(key, row);
    }
  }

  const treeSelection: any[] = [];
  const selectedIds = new Set<string>();
  dataAccessLevels.forEach((dataAccessLevel: any) => {
    const dataFromParent = firstByParent.get(idKey(dataAccessLevel.ParentId));
    if (dataFromParent) {
      treeSelection.push(dataFromParent);
      selectedIds.add(idKey(dataFromParent.id));
      let current = dataFromParent;
      while (current && current.parent) {
        current = firstById(linear.byId, current.parent);
        if (current) {
          if (!selectedIds.has(idKey(current.id))) {
            treeSelection.push(current);
            selectedIds.add(idKey(current.id));
          }
        } else {
          break;
        }
      }
    }
  });

  const result: any[] = [];
  const accessLevel = dataAccessLevels[0].Level;
  for (const row of linear.rows) {
    if (accessLevel < row.level) {
      result.push(row);
    }
  }
  for (const row of treeSelection) {
    result.push(row);
  }
  return result.sort((a: any, b: any) => a.level - b.level);
}

/**
 * Closes the first gap in the level sequence by shifting deeper rows up one level.
 * Uses a loop instead of Math.min(...levels), which overflows the call stack on large arrays.
 */
export function compactOrgLevels(orgs: any[]): void {
  let minLevel = Infinity;
  let maxLevel = -Infinity;
  const existingLevels = new Set();
  for (const org of orgs) {
    minLevel = Math.min(minLevel, org.level);
    maxLevel = Math.max(maxLevel, org.level);
    existingLevels.add(org.level);
  }
  let firstMissing: number | undefined;
  for (let i = minLevel; i <= maxLevel; i++) {
    if (!existingLevels.has(i)) {
      firstMissing = i;
      break;
    }
  }
  if (firstMissing !== undefined) {
    orgs.forEach((org: any) => {
      if (org.level > firstMissing) {
        org.level = org.level - 1;
      }
    });
  }
}
